import type { NodoTesto } from '@email/types.ts';

/**
 * Da quello che si scrive nella casella di testo ai nodi formattati, e
 * ritorno.
 *
 * La notazione e' quella di WhatsApp - *grassetto*, _corsivo_ - perche' e'
 * l'unica che chi scrive gia' conosce senza doverla imparare. Nell'editor ci
 * sono comunque i bottoni: si seleziona il testo e si preme, gli asterischi
 * li mette il programma.
 *
 * I link si scrivono [testo](indirizzo), e un indirizzo scritto da solo
 * diventa cliccabile da se'.
 *
 * Questo file NON produce HTML: produce nodi. L'HTML lo fa il renderer
 * condiviso, che e' anche l'unico punto in cui il testo viene messo in
 * sicurezza. Vale a dire che non c'e' modo, scrivendo nella casella, di far
 * finire codice dentro una email.
 */

const LINK_ESPLICITO = /\[([^\]]+)\]\((https?:\/\/[^\s)]+|mailto:[^\s)]+|tel:[^\s)]+)\)/g;
const URL_NUDO = /(https?:\/\/[^\s<>()[\]]+)/g;
const GRASSETTO = /\*([^*\n]+)\*/g;
const CORSIVO = /_([^_\n]+)_/g;

interface Trovato {
  inizio: number;
  lunghezza: number;
  /** Costruisce il nodo a partire dal contenuto gia' analizzato a sua volta. */
  crea: (dentro: NodoTesto[]) => NodoTesto;
  /** Il pezzo di testo da rianalizzare dentro questo nodo. */
  contenuto: string;
}

/**
 * Il primo pezzo formattato della riga.
 *
 * Si cerca quello che comincia prima; a parita' di inizio vince il piu' lungo,
 * cosi' in "*[sito](indirizzo)*" vince il grassetto e il link ci finisce
 * dentro, invece di lasciare due asterischi orfani nel testo.
 */
function primoTrovato(riga: string): Trovato | null {
  const candidati: Trovato[] = [];

  for (const m of riga.matchAll(LINK_ESPLICITO)) {
    const url = m[2];
    candidati.push({
      inizio: m.index!,
      lunghezza: m[0].length,
      contenuto: m[1],
      crea: (dentro) => ({ t: 'link', url, c: dentro }),
    });
  }

  for (const m of riga.matchAll(URL_NUDO)) {
    const url = m[1];
    candidati.push({
      inizio: m.index!,
      lunghezza: m[0].length,
      contenuto: '',
      crea: () => ({ t: 'link', url, c: [{ t: 'testo', v: url }] }),
    });
  }

  for (const m of riga.matchAll(GRASSETTO)) {
    candidati.push({
      inizio: m.index!,
      lunghezza: m[0].length,
      contenuto: m[1],
      crea: (dentro) => ({ t: 'grassetto', c: dentro }),
    });
  }

  for (const m of riga.matchAll(CORSIVO)) {
    candidati.push({
      inizio: m.index!,
      lunghezza: m[0].length,
      contenuto: m[1],
      crea: (dentro) => ({ t: 'corsivo', c: dentro }),
    });
  }

  if (candidati.length === 0) return null;

  return candidati.sort(
    (a, b) => a.inizio - b.inizio || b.lunghezza - a.lunghezza
  )[0];
}

function rigaInNodi(riga: string): NodoTesto[] {
  const nodi: NodoTesto[] = [];
  let resto = riga;

  while (resto.length > 0) {
    const trovato = primoTrovato(resto);
    if (!trovato) {
      nodi.push({ t: 'testo', v: resto });
      break;
    }

    if (trovato.inizio > 0) {
      nodi.push({ t: 'testo', v: resto.slice(0, trovato.inizio) });
    }

    const dentro = trovato.contenuto ? rigaInNodi(trovato.contenuto) : [];
    nodi.push(trovato.crea(dentro));

    resto = resto.slice(trovato.inizio + trovato.lunghezza);
  }

  return nodi.length > 0 ? nodi : [{ t: 'testo', v: '' }];
}

/** Testo scritto nella casella -> paragrafi formattati. */
export function testoInParagrafi(testo: string): NodoTesto[][] {
  return testo
    .split(/\n\s*\n/)
    .map((p) => p.trim())
    .filter((p) => p !== '')
    .map((p) => rigaInNodi(p.replace(/\n/g, ' ')));
}

function nodoInTesto(nodo: NodoTesto): string {
  switch (nodo.t) {
    case 'testo':
      return nodo.v;
    case 'grassetto':
      return `*${nodo.c.map(nodoInTesto).join('')}*`;
    case 'corsivo':
      return `_${nodo.c.map(nodoInTesto).join('')}_`;
    case 'link': {
      const etichetta = nodo.c.map(nodoInTesto).join('');
      return etichetta === nodo.url ? nodo.url : `[${etichetta}](${nodo.url})`;
    }
  }
}

/** Paragrafi formattati -> testo da rimettere nella casella. */
export function paragrafiInTesto(paragrafi: NodoTesto[][]): string {
  return paragrafi.map((p) => p.map(nodoInTesto).join('')).join('\n\n');
}

/**
 * Avvolge la parte selezionata fra due simboli, come fa il bottone
 * "grassetto". Se non c'e' selezione, inserisce i simboli e mette il cursore
 * in mezzo.
 */
export function avvolgiSelezione(
  testo: string,
  inizio: number,
  fine: number,
  simbolo: string
): { testo: string; inizio: number; fine: number } {
  const selezionato = testo.slice(inizio, fine);

  if (!selezionato) {
    return {
      testo: `${testo.slice(0, inizio)}${simbolo}${simbolo}${testo.slice(fine)}`,
      inizio: inizio + simbolo.length,
      fine: inizio + simbolo.length,
    };
  }

  // Premere due volte toglie la formattazione invece di raddoppiarla.
  if (selezionato.startsWith(simbolo) && selezionato.endsWith(simbolo) && selezionato.length > simbolo.length * 2) {
    const dentro = selezionato.slice(simbolo.length, -simbolo.length);
    return {
      testo: `${testo.slice(0, inizio)}${dentro}${testo.slice(fine)}`,
      inizio,
      fine: inizio + dentro.length,
    };
  }

  return {
    testo: `${testo.slice(0, inizio)}${simbolo}${selezionato}${simbolo}${testo.slice(fine)}`,
    inizio: inizio + simbolo.length,
    fine: fine + simbolo.length,
  };
}
